import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames/bind";
import { nanoid } from "nanoid";
import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../Icon";
import { Portal } from "../Portal";
import { usePrevious } from "../utils/hooks/usePrevious";
import styles from "./Drawer.module.scss";
import DrawerDescriptorWithIcon from "./DrawerDescriptorWithIcon";
import DrawerNextPrevButtons from "./DrawerNextPrevButtons";
import type { DrawerProps } from "./types";

const cx = classNames.bind(styles);
const HORIZONTAL_PADDING = 24;
const ANIMATION_DURATION = 150;
const INITIAL_SHADOW_STYLE = { opacity: 0 };
const INITIAL_DRAWER_STYLE = { width: 0, opacity: 0 };
const WIDTH = { sm: 320, md: 480, lg: 640 };

/**
 * A panel that slides in from the right-hand side of the screen.
 */
const Drawer = ({
  isOpen,
  size = "sm",
  colorVariant = "stone",
  shouldAllowClose = true,
  onClose: onCloseCallback,
  title,
  titleAction,
  iconActionLeft,
  iconActionRight,
  actionRow,
  children,
  rightSlot,
  mode = "blocking",
}: DrawerProps) => {
  const DRAWER_WIDTH = WIDTH[size];

  const titleId = useRef(nanoid());
  const [isVisible, setVisible] = useState(isOpen);
  const [isAnimationComplete, setAnimationComplete] = useState(false);

  const [shadowAnimationStyle, shadowApi] = useSpring(() => ({
    config: { duration: ANIMATION_DURATION },
    ...INITIAL_SHADOW_STYLE,
  }));

  const [drawerAnimationStyle, drawerApi] = useSpring(() => ({
    config: { duration: ANIMATION_DURATION },
    onRest: () => setAnimationComplete(!isAnimationComplete),
    ...INITIAL_DRAWER_STYLE,
  }));

  const startOpenAnimations = () => {
    setVisible(true);

    if (mode === "blocking") shadowApi.start({ opacity: 1 });
    drawerApi.start({ opacity: 1, width: DRAWER_WIDTH });
  };

  const startCloseAnimations = () => {
    if (mode === "blocking") shadowApi.start(INITIAL_SHADOW_STYLE);
    drawerApi.start(INITIAL_DRAWER_STYLE);

    setTimeout(() => {
      setVisible(false);
    }, ANIMATION_DURATION);
  };

  const onClose = () => {
    if (shouldAllowClose) {
      onCloseCallback();
    }
  };

  const previousStateOfIsOpen = !!usePrevious<boolean>(isOpen);

  useEffect(() => {
    // This prevents unnecessary opening and closing of drawer:
    if (previousStateOfIsOpen !== isOpen) {
      if (isOpen) {
        startOpenAnimations();
      } else {
        startCloseAnimations();
      }
    }
  }, [previousStateOfIsOpen, isOpen]);

  return (
    <>
      {isVisible ? (
        <Portal>
          <div className={cx("container", { isOpen }, mode)}>
            <>
              {mode === "blocking" && (
                <animated.div
                  className={cx("shadow")}
                  style={shadowAnimationStyle}
                  onClick={onClose}
                />
              )}

              <animated.div
                className={cx(
                  "drawer",
                  colorVariant && `is-color-${colorVariant}`
                )}
                style={drawerAnimationStyle}
                role="dialog"
                aria-labelledby={titleId.current}
              >
                <div className={cx("header")}>
                  <div className={cx("titleSection")}>
                    {iconActionLeft ? (
                      <div className={cx("iconAction")}>{iconActionLeft}</div>
                    ) : null}
                    <h5
                      className={cx("title")}
                      id={titleId.current}
                    >
                      {title}
                    </h5>
                    {iconActionRight ? (
                      <div className={cx("iconAction")}>{iconActionRight}</div>
                    ) : null}
                  </div>

                  {titleAction ? (
                    <div className={cx("titleAction")}>{titleAction}</div>
                  ) : null}

                  {shouldAllowClose ? (
                    <button
                      onClick={onClose}
                      className={cx("closeButton")}
                      aria-label="Close"
                    >
                      <Icon
                        variant="x"
                        size={24}
                      />
                    </button>
                  ) : null}
                </div>

                {isAnimationComplete ? (
                  <div
                    className={cx("content")}
                    style={{ minWidth: DRAWER_WIDTH - HORIZONTAL_PADDING * 2 }}
                  >
                    {children}
                  </div>
                ) : null}

                {actionRow ? (
                  <div className={cx("footer")}>
                    {Array.isArray(actionRow)
                      ? actionRow.map((action) => action)
                      : actionRow}
                  </div>
                ) : null}
              </animated.div>
              {rightSlot && <div>{rightSlot}</div>}
            </>
          </div>
        </Portal>
      ) : null}
    </>
  );
};

Drawer.NextPrevButtons = DrawerNextPrevButtons;
Drawer.DescriptorWithIcon = DrawerDescriptorWithIcon;

export default Drawer;
